// Hotel booking detail. Same .detail-wrap shell as the flight one.
//
// It reads from two places on purpose:
//
//   /v1/bookings/<id>/         — our own row: status, amounts, guests, and the
//                                `hotel_details` snapshot taken at book time.
//   /v1/hotels/booking/<id>/   — the live itinerary, for the full room and
//                                cancellation detail.
//
// The second can legitimately fail: a hotel quote session is short-lived, and a
// booking from last week has no session left. That is why the page renders from
// the stored snapshot and treats the live call as enrichment — a confirmed stay
// must still open a month later.

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useBodyClass } from "../lib/useBodyClass";
import {
  fmtBoard, fmtDateFull, fmtDeadline, fmtMoney, nightsLabel, occupancyLabel, starLabel,
} from "../lib/hotelFormat";
import type { HotelBookingSummary, HotelItinerary } from "../lib/hotelTypes";
import type { BookingListItem, BookingStatus } from "../lib/types";

export function HotelBookingDetail() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();

  const meta = useQuery({
    queryKey: ["bookingMeta", bookingId],
    queryFn: () => api.get<BookingListItem>(`/v1/bookings/${bookingId}/`),
    enabled: !!bookingId,
  });
  const itin = useQuery({
    queryKey: ["hotelBooking", bookingId],
    queryFn: () => api.get<HotelItinerary>(`/v1/hotels/booking/${bookingId}/`),
    enabled: !!bookingId,
    retry: false,
  });

  if (meta.isLoading) {
    return <div className="app-loading"><span className="spinner" />Loading booking…</div>;
  }
  if (meta.error || !meta.data) return <div className="app-error">Couldn't load booking.</div>;

  const b = meta.data;
  const stay = (b.hotel_details || {}) as HotelBookingSummary;
  const live = itin.data;
  const guests = (b.passengers || []) as Array<Record<string, unknown>>;
  const stars = starLabel(stay.star_rating ?? live?.hotel.star_rating);
  const currency = live?.pricing.currency || b.currency;
  const room = live?.room;
  const occupancies = live?.occupancies || stay.occupancies || [];
  const reference = stay.hotel_confirmation_code || live?.hotel_confirmation_code;

  return (
    <div className="container">
      <div className="detail-wrap">
        <div className="crumb-line">
          <Link to="/bookings">My stays</Link>
          <span className="sep">›</span>
          <b>{reference || b.id.slice(0, 8)}</b>
        </div>

        <div className="detail-head">
          <div className="detail-pnr">{reference || "—"}</div>
          <span className={`status-pill ${statusClass(b.status)}`}>
            <span className="dot" />{statusLabel(b.status)}
          </span>
        </div>
        <p className="detail-sub">
          Booking ID <b>{b.id.slice(0, 8)}</b>
          &nbsp;·&nbsp; Updated <b>{fmtIssued(b.modified_at)}</b>
          {b.pnr && <> &nbsp;·&nbsp; Supplier ref <b>{b.pnr}</b></>}
        </p>

        <div className="detail-grid">
          <div>
            <div className="itin-card">
              <h3>Your <span className="serif">stay</span></h3>
              <div className="itin-card-sub">
                {stars && <span className="hotel-stars">{stars}</span>}
                {stay.name || live?.hotel.name || "—"}
                {stay.city_name ? ` · ${stay.city_name}` : ""}
              </div>

              {itin.isLoading && (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                  <span className="spinner" />Loading live details…
                </div>
              )}
              {itin.error instanceof ApiError && (
                <div style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 12 }}>
                  {/* Expected on an older booking — the quote session is gone,
                      not the booking. Say so rather than showing an error. */}
                  The supplier's live quote for this stay has expired — showing the
                  details recorded at booking time.
                </div>
              )}

              <div className="hotel-pay-dates">
                <div>
                  <span className="lbl">Check-in</span>
                  <b>{fmtDateFull(stay.check_in || live?.check_in)}</b>
                  {(stay.check_in_time || live?.hotel.check_in_time) && (
                    <span className="t">from {stay.check_in_time || live?.hotel.check_in_time}</span>
                  )}
                </div>
                <div className="hotel-pay-nights">
                  {nightsLabel(stay.nights ?? live?.nights ?? 0)}
                </div>
                <div>
                  <span className="lbl">Check-out</span>
                  <b>{fmtDateFull(stay.check_out || live?.check_out)}</b>
                  {(stay.check_out_time || live?.hotel.check_out_time) && (
                    <span className="t">by {stay.check_out_time || live?.hotel.check_out_time}</span>
                  )}
                </div>
              </div>

              <div className="itin-detail" style={{ marginTop: 14 }}>
                <span>
                  <b>{stay.room_name || room?.name || "Room"}</b> ·{" "}
                  {fmtBoard(stay.board_basis || room?.board_basis, stay.board_name || room?.board_name)}
                </span>
                {!!occupancies.length && <span>{occupancyLabel(occupancies)}</span>}
                <span className={`tag ${(stay.refundable ?? room?.refundable) ? "refundable" : "non-refundable"}`}>
                  {(stay.refundable ?? room?.refundable) ? "Free cancellation" : "Non-refundable"}
                </span>
              </div>

              {(stay.free_cancellation_until || live?.last_free_cancellation_date) && (
                <div className="room-cancel ok" style={{ marginTop: 12 }}>
                  Free cancellation until{" "}
                  <b>{fmtDeadline(live?.last_free_cancellation_date || stay.free_cancellation_until)}</b>
                </div>
              )}

              {stay.address && (
                <div className="hotel-contact">
                  <b>Address</b>
                  <span>{stay.address}</span>
                </div>
              )}
              {live?.hotel && (live.hotel.phone || live.hotel.email) && (
                <div className="hotel-contact">
                  <b>Property contact</b>
                  <span>{[live.hotel.phone, live.hotel.email].filter(Boolean).join(" · ")}</span>
                </div>
              )}
            </div>

            <div className="itin-card">
              <h3>Guests <span className="serif">on the booking</span></h3>
              <div className="itin-card-sub">
                One named guest per room, as the property requires. Everyone else in a
                room travels with them on the booking note.
              </div>
              {guests.length === 0 ? (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                  No guests recorded against this booking.
                </div>
              ) : guests.map((guest, i) => {
                const others = (guest.other_occupants || []) as Array<Record<string, unknown>>;
                return (
                  <div className="passenger-line" key={i}>
                    <div>
                      <b>
                        Room {String(guest.occupancy_number ?? i + 1)} ·{" "}
                        {[guest.title, guest.first_name, guest.last_name]
                          .filter(Boolean).map(String).join(" ")}
                      </b>
                      <span>
                        {others.length
                          ? `with ${others.map((o) => String(o.first_name ?? "")).filter(Boolean).join(", ")}`
                          : "sole named guest"}
                        {guest.email ? ` · ${String(guest.email)}` : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="itin-card">
              <h3>Manage <span className="serif">this booking</span></h3>
              <div className="itin-card-sub">
                {/* Honest about scope: the supplier supports cancellation, this
                    build doesn't expose it yet. */}
                Cancellation isn't wired into this build — contact us to cancel or amend.
              </div>
            </div>
          </div>

          <aside>
            <div className="summary-card">
              <h3>Payment</h3>
              <div className="sub">Captured from wallet · {fmtIssued(b.modified_at)}</div>
              <div className="fare-line"><span className="lbl">Method</span><span>Partner wallet</span></div>
              <div className="fare-line">
                <span className="lbl">Status</span>
                <span className={`status-pill ${statusClass(b.status)}`}>
                  <span className="dot" />{statusLabel(b.status)}
                </span>
              </div>
              <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />
              <div className="fare-line total">
                <span className="lbl">Amount</span>
                <span>{b.amount_charged ? fmtMoney(parseFloat(b.amount_charged), currency) : "—"}</span>
              </div>
              {b.wallet_debited_inr && (
                <div className="fare-line" style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  <span className="lbl">Wallet debit (INR)</span>
                  <span>{fmtMoney(parseFloat(b.wallet_debited_inr), "INR")}</span>
                </div>
              )}
              {!!live?.pricing.taxes_at_property && (
                <div className="fare-line at-property-line">
                  <span className="lbl">Payable at the property</span>
                  <span>{fmtMoney(live.pricing.taxes_at_property, currency)}</span>
                </div>
              )}
            </div>

            <div className="summary-card" style={{ marginTop: 16 }}>
              <h3>References</h3>
              <div className="fare-line">
                <span className="lbl">Hotel confirmation</span>
                <span style={{ fontWeight: 700 }}>{reference || "—"}</span>
              </div>
              <div className="fare-line">
                <span className="lbl">Supplier ref</span>
                <span>{b.pnr || live?.booking_reference || "—"}</span>
              </div>
              <div className="fare-line">
                <span className="lbl">Supplier</span>
                <span style={{ textTransform: "capitalize" }}>{b.provider}</span>
              </div>
              <p className="field-help" style={{ marginTop: 10 }}>
                The property knows this stay by the <b>hotel confirmation</b> code —
                the supplier ref is for us and for support.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function statusClass(status: BookingStatus): string {
  if (status === "confirmed") return "confirmed";
  if (status === "cancelled") return "refunded";
  if (status === "failed") return "failed";
  if (status === "draft") return "draft";
  return "pending";
}

function statusLabel(status: BookingStatus): string {
  const labels: Record<BookingStatus, string> = {
    draft: "Draft",
    passenger_saved: "Guests saved",
    priced: "Re-priced",
    confirmed: "Confirmed",
    cancelled: "Cancelled",
    failed: "Failed",
  };
  return labels[status] || status;
}

function fmtIssued(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}
